import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'

export type NotificationType =
    | 'NON_COMPLIANT'
    | 'EXPIRED'
    | 'EXPIRING_SOON'
    | 'MISSING_COI'

export interface VendorUploadInfo {
    vendor_name: string
    vendor_id: number
    apartment_name: string
    apartment_id: number
    apartment_names?: string[]
    notification_type: NotificationType
    compliance_issues: string[]
    expires_at: string
    already_uploaded: boolean
}

export interface VendorCOIUploadResponse {
    success: boolean
    message: string
    vendor_coi_id: number
}

export const useVendorCOIUpload = (token: string | null) => {
    // Get upload page info
    const {
        data: uploadInfo,
        isLoading: isLoadingInfo,
        error: errorInfo,
        refetch: refetchInfo,
    } = useQuery<AxiosResponse<VendorUploadInfo>, Error>({
        queryKey: ['vendorCOIUpload', token],
        queryFn: () =>
            axiosInstance.get(`/company/vendor-coi-upload/?token=${token}`),
        enabled: !!token,
        retry: false,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Submit COI upload
    const uploadCOI = useMutation({
        mutationFn: async ({ file, name }: { file: File; name?: string }) => {
            const formData = new FormData()
            formData.append('token', token || '')
            formData.append('file', file)
            if (name) {
                formData.append('name', name)
            }

            return axiosInstance.post<VendorCOIUploadResponse>(
                '/company/vendor-coi-upload/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            toast.success('Certificate of Insurance uploaded successfully!')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload certificate'
            toast.error(errorMessage)
        },
    })

    return {
        uploadInfo: uploadInfo?.data || null,
        isLoadingInfo,
        errorInfo,
        refetchInfo,
        uploadCOI,
        isUploading: uploadCOI.isLoading,
    }
}
